import type { ColDef, RowClickedEvent, ValueFormatterParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import { Button, Tab, Tabs } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { ReservationState } from '../../../modules/enums/types';
import type { MewsReservation } from '../../../modules/interfaces';
import { roomSort } from '../../../modules/utils/tableSorts';
import { BadgeCellRenderer, DataTable } from '../../Tables/DataTable';

interface PropertyConfigModalProps {
  selectedReservations?: MewsReservation[];

  show: boolean;
  handleClose: () => void;
  setConfirmedReservation?: (reservation: MewsReservation) => void;
}

export const paymentStatusColorMapper: Record<string, string> = {
  CONFIRMED: 'primary',
  STARTED: 'warning',
};

export const GuestReservationTableModal: FC<PropertyConfigModalProps> = ({
  selectedReservations,
  show,
  handleClose,
  setConfirmedReservation,
}) => {
  const [searchString] = useState<string>('');

  const [activeReservations, setActiveReservations] = useState<MewsReservation[]>([]);
  const [changeState, setChangeState] = useState<number>(0);
  const [confirmedReservations, setConfirmedReservations] = useState<MewsReservation[]>(
    [],
  );
  const [pastReservations, setPastReservations] = useState<MewsReservation[]>([]);
  const onClose = () => {
    handleClose();
  };

  const handleBookingDetailsData = (item: MewsReservation) => {
    if (setConfirmedReservation) {
      setConfirmedReservation(item);
      handleClose();
    }
  };

  useEffect(() => {
    if (!selectedReservations || selectedReservations.length === 0) return;
    const confirmed = selectedReservations.filter(
      (reservation) => reservation.State === ReservationState.CONFIRMED,
    );

    const active = selectedReservations.filter(
      (reservation) => reservation.State === ReservationState.STARTED,
    );

    const past = selectedReservations.filter(
      (reservation) => reservation.State === ReservationState.PROCESSED,
    );

    setConfirmedReservations(confirmed);
    setActiveReservations(active);
    setPastReservations(past);
  }, [selectedReservations]);

  const [columns] = useState<ColDef<MewsReservation>[]>([
    {
      valueGetter: (params) => {
        const { data: reservation } = params;
        const resource = reservation?.Resource;
        const name = resource
          ? `${resource.Name} -  ${resource.ResourceName}`
          : 'Not assigned';

        return name;
      },
      headerName: 'ROOM',
      flex: 1,
      sortable: true,
      comparator: (valueA, valueB) => roomSort(valueA, valueB),
    },

    {
      field: 'ScheduledStartUtc',
      headerName: 'ARRIVAL DATE',
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<MewsReservation, string>) => {
        const { value } = params;
        const formattedDate = DateTime.fromISO(value || '').toFormat('dd LLL yyyy');

        return formattedDate;
      },
      cellStyle: {
        color: '#757474',
      },
      flex: 1,
    },
    {
      field: 'EndUtc',
      headerName: 'DEPARTURE DATE',
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<MewsReservation, string>) => {
        const { value } = params;
        const formattedDate = DateTime.fromISO(value || '').toFormat('dd LLL yyyy');

        return formattedDate;
      },
      cellStyle: {
        color: '#757474',
      },
      flex: 1,
    },

    {
      field: 'State',
      headerName: 'STATUS',
      sortable: true,
      cellRenderer: BadgeCellRenderer,
      flex: 1,
    },
  ]);

  const reservationTable = (item: MewsReservation[], tap: string) => {
    return item && item.length > 0 ? (
      <DataTable
        searchString={searchString}
        changeState={changeState}
        rowData={item}
        columnDefs={columns}
        pagination
        paginationPageSize={5}
        onRowClicked={(e: RowClickedEvent<MewsReservation>) =>
          handleBookingDetailsData(e.data!)
        }
      />
    ) : (
      <div className="no-reservation-note-text">{`There are no ${tap} reservations`}</div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
      }}
      size="xl"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title className="h5">Guest Reservations</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          defaultActiveKey="active"
          className="admin-tabs"
          onClick={() => {
            setChangeState(changeState + 1);
          }}
        >
          <Tab eventKey="active" title="Active" className="admin-tab">
            {reservationTable(activeReservations, 'active')}
          </Tab>
          <Tab eventKey="confirmed" title="Upcoming" className="admin-tab">
            {reservationTable(confirmedReservations, 'pending')}
          </Tab>

          <Tab eventKey="past" title="Past" className="admin-tab">
            {reservationTable(pastReservations, 'past')}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div>
          <Button className="cancel-button" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
